// adapted libs/social/api/src/lib/pagesets.ts

import { OperationStatus } from '@pressreader/appstore';
import { httpGet, httpPost, httpPut } from '@pressreader/services';
import { isNonNullable } from '@pressreader/types';

import type { IssuePageSetDto, LinkDto } from '../types';

import { defaultSnippetRendererUrl, issuesApiEndpointBaseUrl, jsonBodyParam, pagesetsApiEndpointBaseUrl } from './constants';

interface IssuePageSelection {
    issue: string;
    pages: number[];
}

function getPageSetData(pageSetId: string): Promise<IssuePageSetDto> {
    return httpGet<IssuePageSetDto>(pagesetsApiEndpointBaseUrl, pageSetId);
}

function createPageSet(params: IssuePageSelection): Promise<IssuePageSetDto> {
    return httpPost(pagesetsApiEndpointBaseUrl, '', params, jsonBodyParam);
}

function updatePageSetData(pageSetId: string, pages: { pages: number[] }): Promise<void> {
    return httpPut(pagesetsApiEndpointBaseUrl, pageSetId, pages, jsonBodyParam);
}

function getIssuePageSetsApi(issueKey: string): Promise<{ pageSets: IssuePageSetDto[] }> {
    return httpGet(issuesApiEndpointBaseUrl, `${issueKey}/pagesets`);
}

function getPageSetDirectLink(pageSetId: string): Promise<LinkDto> {
    return httpGet(pagesetsApiEndpointBaseUrl, `${pageSetId}/directlink`);
}

async function createHttpRequest(method: 'GET' | 'POST', url: string): Promise<{ status: number; location: string }> {
    if (!method) {
        throw new Error('Method not specified');
    }
    if (!url) {
        throw new Error('Url not specified');
    }

    return new Promise((resolve, reject) => {
        const client = new XMLHttpRequest();
        client.open(method, url, true);
        client.onreadystatechange = () => {
            try {
                if (client.readyState !== XMLHttpRequest.HEADERS_RECEIVED) {
                    return;
                }

                const location = client.getResponseHeader('Location');
                if (!isNonNullable(location)) {
                    throw new Error('Location header not found');
                }
                resolve({ status: client.status, location });
            } catch (e) {
                reject(e);
            }
        };
        client.send();
    });
}

async function createPageSetSnippetData({ pageSetId, baseUrl = defaultSnippetRendererUrl }: { pageSetId: string; baseUrl: string }): Promise<{
    status: OperationStatus;
    location: string;
}> {
    return createHttpRequest('POST', `${baseUrl}/pagesets/${pageSetId}/snippet`);
}

function requestSnippetStatusData(statusUrl: string): Promise<{ status: OperationStatus; location: string }> {
    return createHttpRequest('GET', statusUrl);
}

export {
    createPageSet,
    createPageSetSnippetData,
    getIssuePageSetsApi,
    getPageSetData,
    getPageSetDirectLink,
    requestSnippetStatusData,
    updatePageSetData,
};
